import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Popper } from '@mui/material';

import { getVideoQualityFromLocalStorage, setVideoQualityToLocalStorage } from '../../../helpers';
import { updateSelectedVideoQuality } from '../../../store/sharedStore/sharedStoreSlice';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/settings.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import styles from './VideoQualitySelector.module.scss';
import { VIDEO_TYPE } from '../../../enums';

type TVideoQualitySelectorProps = {
  videoData?: VIDEO_TYPE;
  showLabel?: boolean;
  style?: any;
};

const VideoQualitySelector: React.FC<TVideoQualitySelectorProps> = ({ videoData, showLabel, style }) => {
  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const { selectedVideoQuality } = useAppSelector((state) => state.sharedStore);

  const onVideoQualitySet = (videoQualityArr: [string, string]) => {
    setAuto(false);
    // videoQualityArr: ['1080', 'www.video.com/video.mp4']
    dispatch(updateSelectedVideoQuality({ quality: videoQualityArr[0], url: videoQualityArr[1] }));
    setVideoQualityToLocalStorage(`${videoQualityArr[0]}`);
  };

  const switchShowSelector = () => {
    setShow(!show);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    if (selectorRef.current?.contains(e.target as Node)) return;
    e.stopPropagation();
    setShow(false);
  });

  const [auto, setAuto] = useState(false);

  const onAutoSelect = () => {
    setAuto(true);
    let highestKey = null;
    let highestValue = null;
    if (videoData) {
      for (const key in videoData?.s3_keys) {
        if (!highestKey || (parseInt(key) > parseInt(highestKey) && videoData.s3_keys[key].length > 0)) {
          highestKey = key;
          highestValue = videoData.s3_keys[key];
        }
      }

      if (highestKey && highestValue) {
        dispatch(updateSelectedVideoQuality({ quality: highestKey, url: highestValue }));
        setVideoQualityToLocalStorage(`${highestKey}`);
      }
    }
  };

  const [speedPos, setSpeedPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (actionMenuRef.current) {
      const rect = actionMenuRef.current.getBoundingClientRect();
      setSpeedPos({ top: rect.top, left: rect.left });
    }
  }, [show, actionMenuRef]);

  const selector = (
    <div
      ref={selectorRef}
      className={cx(styles.selector, show && styles.shownSelector)}
      style={showLabel ? { ...style, position: 'static' }
        : { top: `${speedPos.top - 50}px`, left: `${speedPos.left + 15}px`, ...style, position: 'fixed' }}
      key={videoData?.id}
    >
      <div className={styles.list}>
        {videoData?.s3_keys &&
          Object.entries(videoData?.s3_keys)
            .reverse()
            .map(
              (quality) =>
                quality[1].length > 0 && (
                  <div
                    className={cx(
                      styles.option,
                      selectedVideoQuality && +selectedVideoQuality?.quality === +quality[0] && styles.selectedOption,
                    )}
                    onClick={() => onVideoQualitySet(quality)}
                    key={`${quality[0]}`}
                  >
                    {quality[0]}
                  </div>
                ),
            )}

        <div className={cx(styles.option, auto ? styles.selectedOption : '')} onClick={onAutoSelect} key={'auto'}>
          Auto
        </div>
      </div>
    </div>
  );

  return (
    <div className={cx(styles.wrapper, showLabel && styles.toolbar)} ref={actionMenuRef} onClick={switchShowSelector}>
      <div className={styles.qualityIcon} onClick={switchShowSelector}>
        <SettingsIcon />
        {selectedVideoQuality && <div className={styles.mediumIcon}> {selectedVideoQuality.quality} </div>}

        {showLabel && (
          <div className={styles.qualityLabel}>
            Video <br /> Quality
          </div>
        )}
      </div>
      {showLabel ? <Popper open={show} anchorEl={actionMenuRef.current} placement="top-end"
        className={styles.wrapper} sx={{ zIndex: 1300 }}
        container={() => document.fullscreenElement || document.body}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}>
        {selector}
      </Popper> : selector}
    </div>
  );
};

export default VideoQualitySelector;
